import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { PageTransition } from '@/components/PageTransition';
import { Building2, Loader2, ArrowLeft, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';

const CreateCompanyPage = () => {
  const navigate = useNavigate();
  const { user, userProfile } = useAuth();
  usePageTitle('Create Company');

  const [companyName, setCompanyName] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const trimmedName = companyName.trim();
    if (!trimmedName) {
      setError('Company name is required');
      return;
    }

    setIsCreating(true);

    try {
      // Step 1: Create the company
      const { data: newCompany, error: companyError } = await supabase
        .from('companies')
        .insert({ name: trimmedName })
        .select('id, name')
        .single();

      if (companyError) throw companyError;

      // Step 2: Create a new user_profile for this company (copies personal info from current profile)
      const { data: newProfile, error: profileError } = await supabase
        .from('user_profiles')
        .insert({
          auth_id: user.id,
          email: userProfile?.email || user.email,
          first_name: userProfile?.first_name || '',
          last_name: userProfile?.last_name || '',
          display_name: userProfile?.display_name || '',
          avatar_url: userProfile?.avatar_url || null,
          company_id: newCompany.id,
          company_name: trimmedName,
          role: 'Admin'
        })
        .select('id')
        .single();

      if (profileError) {
        // Cleanup: delete the company we just created
        await supabase.from('companies').delete().eq('id', newCompany.id);
        throw profileError;
      }

      // Step 3: Create company_members entry as Admin with full permissions
      // '545dcad2-4e3b-4eea-80ef-0293f5977bf2' is the system "Company Admin" permission group (["*"])
      const COMPANY_ADMIN_PERMISSION_GROUP = '545dcad2-4e3b-4eea-80ef-0293f5977bf2';

      const { error: memberError } = await supabase
        .from('company_members')
        .insert({
          company_id: newCompany.id,
          user_id: newProfile.id,
          role: 'Admin',
          auth_id: user.id,
          permission_group_id: COMPANY_ADMIN_PERMISSION_GROUP
        });

      if (memberError) {
        // Cleanup
        await supabase.from('user_profiles').delete().eq('id', newProfile.id);
        await supabase.from('companies').delete().eq('id', newCompany.id);
        throw memberError;
      }

      toast.success(`${trimmedName} created successfully!`);

      // Set the new profile as active so AuthContext loads it on reload
      localStorage.setItem('training_matrix_active_profile_id', newProfile.id);

      // Full reload to pick up the new profile in AuthContext
      window.location.href = '/dashboard';
    } catch (err) {
      console.error('Error creating company:', err);
      setError(err.message || 'Failed to create company. Please try again.');
      setIsCreating(false);
    }
  };

  return (
    <PageTransition>
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="mb-4 text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>

          <Card>
            <CardHeader className="text-center">
              <div className="mx-auto mb-4 h-14 w-14 rounded-full bg-violet-100 flex items-center justify-center">
                <Building2 className="h-7 w-7 text-violet-600" />
              </div>
              <CardTitle className="text-xl">Create a New Company</CardTitle>
              <CardDescription>
                Set up a new company workspace. You'll be the Admin.
              </CardDescription>
            </CardHeader>

            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                {error && (
                  <Alert variant="destructive">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                )}

                <div className="space-y-2">
                  <Label htmlFor="companyName">Company Name</Label>
                  <Input
                    id="companyName"
                    placeholder="Enter your company name"
                    value={companyName}
                    onChange={(e) => {
                      setCompanyName(e.target.value);
                      if (error) setError('');
                    }}
                    className="h-11"
                    required
                    disabled={isCreating}
                    autoFocus
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full h-11"
                  disabled={isCreating || !companyName.trim()}
                >
                  {isCreating ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <Building2 className="h-4 w-4 mr-2" />
                      Create Company
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </div>
    </PageTransition>
  );
};

export default CreateCompanyPage;
